import {
  FC,
  HTMLAttributes,
  MouseEventHandler,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import {
  CheckOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  LockOutlined,
  PlusOutlined,
  StarFilled,
  StarOutlined,
  TagOutlined,
} from '@ant-design/icons';
import {
  Button,
  Popconfirm,
  Popover,
  PopoverProps,
  Tooltip,
} from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { AccesModal, AddReportTGModal, EditModal, InternalTagAttachModal, ModalKey, ThunkDispatch } from './modals';
import {
  copyReportOrDashboard,
  deleteReportOrDashboard,
  setMainDashboardThunk,
  toggleFavorite,
} from '../../../../../../app/providers/redux/slices/reports/params/reports/reportsSlice';
import { ROLES } from '../../../../../../helpers/utils/auth';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../../../../helpers/utils/ui';
import { tgApi } from '../../../../api/endpoints/tgApi';
import { routeNames } from '../../../../constants/routes';
import { useActions } from '../../../../hooks/useActions';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';
import { ItemTypee, KEY_COHGORT, KEY_WEBINAR_QUALITY } from '../TabContent';

const modalButtonStyle: HTMLAttributes<HTMLButtonElement>['style'] = {
  outline: 'none',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: '4px 8px',
  display: 'flex',
  alignItems: 'center',
  transition: 'background 0.3s',
  boxShadow: 'none'
};

interface PopoverContentProps {
  item: ItemTypee;
  isDashboard: boolean;
  trigger?: PopoverProps['trigger']
}

interface MenuItem<T = any> {
  key: ModalKey;
  text: ReactNode;
  tooltipText?: string
  Icon: FC<any>;
  onClick?: MouseEventHandler<HTMLElement>;
  modal?: FC<T>;
  modalProps?: T;
  disabled?: boolean;
}

const MenuItem: FC<MenuItem> = ({
  text,
  tooltipText = 'Недостаточно прав',
  Icon,
  onClick,
  disabled = false
}) => {
  return (
    <Tooltip title={disabled ? tooltipText : ''} trigger={'hover'} placement='right'>
      <Button
        style={modalButtonStyle}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
        disabled={disabled}
      >
        <Icon style={{ fontSize: 15, marginRight: '8px' }} />
          <span style={{ fontSize: 15 }}>{text} {disabled && <LockOutlined />}</span>
      </Button>
    </Tooltip>
  );
};

const renderMenuItem = (props: (MenuItem | null)) => (
  props ? <MenuItem {...props} key={props.key} /> : null
);

const SuperPermission = new Set([ ROLES.admin, ROLES.super, ROLES.userSettings ]);

export const ReportMenu: FC<PopoverContentProps> = ({ item, isDashboard, trigger = 'click' }) => {
  const dispatch: ThunkDispatch = useDispatch();
  const navigate = useNavigate();
  const {
    setReportFavorite,
    setDashboardFavorite,
    deleteReport
  } = useActions();

  const schoolId = useTypedSelector(state => state.currentSchool.selectedSchool?.id);
  const currentUserRole = useTypedSelector(state => state.currentUser.role);
  const userId = useTypedSelector(state => state.currentUser.id);
  const isCompare = useTypedSelector(state => state.meta.isCompare);
  const mainHash = useTypedSelector(state => state.reports.mainDashboardHash);
  
  const [ modalVisible, setModalVisible ] = useState<
  Partial<Record<ModalKey, boolean>>
  >({
    [ModalKey.Rename]: false,
    [ModalKey.Tags]: false,
    [ModalKey.SendTG]: false,
    [ModalKey.Accesses]: false,
  });
  const [ isOpen, setIsOpen ] = useState(false);
  
  const adminPermission = SuperPermission.has(currentUserRole);
  const canDoAction = item.user_id === userId || adminPermission;

  const itSCANReport = item.access_id === 3;
  const havePerrmissonToSCAN = itSCANReport ? currentUserRole === ROLES.super : true;
  const isStaticReport = !!item.static;
  
  const handleToggleFavorite: MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    const newFavoriteStatus = !item.is_favorite;
    const setFavorite =
      isDashboard ? setDashboardFavorite : setReportFavorite;

    setFavorite({
      id: item.id,
      isFavorite: newFavoriteStatus,
    });

    dispatch(toggleFavorite({ id: item.id, isFavorite: item.is_favorite }))
      .unwrap()
      .catch(() => {
        setFavorite({
          id: item.id,
          isFavorite: !newFavoriteStatus,
        });
      });
  };
  const handeCopyReport: MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    let reportLink = `${process.env.REACT_APP_BASE_URL}${location.pathname}?report_id=${item.hash}`;
    
    if (item.id === KEY_COHGORT) {
      reportLink = `${process.env.REACT_APP_BASE_URL}${routeNames.reportCohort}`;
    }
    if (item.id === KEY_WEBINAR_QUALITY) {
      reportLink = `${process.env.REACT_APP_BASE_URL}${routeNames.reportWebinarQuality}`;
    }

    navigator.clipboard
      .writeText(reportLink)
      .then(() => {
        showSuccessMessage('Ссылка скопирована');
      })
      .catch(() => {
        showErrorMessage('Ошибка при копировании ссылки отчета');
      });
  };
  const handleCopyReportOrDashboard: MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();

    if (!schoolId) {
      showErrorMessage('Школа не выбрана');
      return;
    }
    const newName = `${isDashboard? item.values?.name : item.name} (копия)`;
    dispatch(
      copyReportOrDashboard({
        schoolId,
        id: item.id,
        isDashboard,
        newName,
        values: item.values,
        pathName: location.pathname,
        callBack: (reportId: string) => {setIsOpen(false);navigate(location.pathname + '?report_id=' + reportId);} 
      })
    );
  };

  const handleRemoveReport = () => {
    if (!isCompare && schoolId) {

      deleteReport(schoolId, {
          id: item.id
        });
    }
  };

  const handleAddAtMain = () => {
    dispatch(setMainDashboardThunk(item.hash));
  };


  const Actions: (MenuItem | null)[] = [
    !isStaticReport ?{
      key: ModalKey.Favorite,
      text: item?.is_favorite ? 'Убрать из избранного' : 'В избранное',
      Icon: item?.is_favorite ? StarFilled : StarOutlined,
      onClick: handleToggleFavorite,
    } : null,
    havePerrmissonToSCAN && !isStaticReport ? {
      key: ModalKey.Rename,
      text: 'Переименовать',
      Icon: EditOutlined,
      onClick: () =>{
        setModalVisible((prev) => ({ ...prev, [ModalKey.Rename]: true }));
        setIsOpen(false);
      },
      disabled: !canDoAction || !havePerrmissonToSCAN,
      modal: EditModal,
      modalProps: {
        item,
        isDashboard,
      },
    } : null,
    {
      key: ModalKey.CopyLink,
      text: 'Копировать ссылку',
      Icon: EditOutlined,
      onClick: handeCopyReport,
    },
    !isStaticReport ? {
      key: ModalKey.CopyData,
      text: isDashboard ? 'Копировать дашборд' : 'Копировать отчёт',
      Icon: EditOutlined,
      onClick: handleCopyReportOrDashboard,
    } : null,
    havePerrmissonToSCAN && !isStaticReport ? {
      key: ModalKey.Tags,
      text: 'Теги',
      Icon: TagOutlined,
      onClick: () =>{
        setModalVisible((prev) => ({ ...prev, [ModalKey.Tags]: true }));
        setIsOpen(false);
      },
      modal: InternalTagAttachModal,
      disabled: !canDoAction || !havePerrmissonToSCAN,
      modalProps: {
        isDashboard,
        item,
      },
    } : null,
    havePerrmissonToSCAN && adminPermission ? {
      key: ModalKey.Accesses,
      text: 'Доступ',
      Icon: LockOutlined,
      onClick: () =>{
        setModalVisible((prev) => ({ ...prev, [ModalKey.Accesses]: true }));
        setIsOpen(false);
      },
      disabled: !canDoAction || !havePerrmissonToSCAN,
      modal: AccesModal,
      modalProps: {
        item,
        isDashboard,
      },
    } : null,
  ];

  const dashboardActions: MenuItem[] = [
    item.hash !== mainHash ? {
      key: ModalKey.Add,
      text: 'Добавить в сводку',
      Icon: PlusOutlined,
      onClick: handleAddAtMain,
    } : {
      key: ModalKey.Add,
      text: 'Уже в сводке',
      tooltipText: 'Уже в сводке',
      Icon: CheckOutlined,
      onClick: () => {},
      disabled: true,
    }
  ];

  const reportActions: (MenuItem | null)[] = [
    !isStaticReport ? {
      key: ModalKey.SendTG,
      text: 'Отправка в TG',
      Icon: ClockCircleOutlined,
      onClick: () =>{
        setModalVisible((prev) => ({ ...prev, [ModalKey.SendTG]: true }));
        setIsOpen(false);
      },
      modal: AddReportTGModal,
      modalProps: {
        report: {
          visible: true,
          title: item?.name,
          id: item?.id,
        },
      },
    } : null,
  ];

  const removeAction = {
    key: ModalKey.RemoveAction,
    text: 'Удалить',
    Icon: DeleteOutlined,
    disabled: !canDoAction || !havePerrmissonToSCAN,
  };

  const currentActions = isDashboard ? dashboardActions : reportActions;
  const allActions = [ ...Actions, ...currentActions ];
  const menuActions = allActions.map(renderMenuItem);
  const modalActions = allActions.map((Action) => {
    if (!Action) return null;

    const Modal = Action.modal;
    return Modal ? (
      <Modal
        visible={modalVisible[Action.key]}
        setVisible={(visible: boolean) =>
          setModalVisible((prev) => ({ ...prev, [Action.key]: visible }))
        }
        {...Action.modalProps}
      />
    ) : null;
  });

  const isVisible = Object.values(modalVisible).some(Boolean)
    ? false
    : true;
    
  return (
    <>{isVisible && 
      <Popover
        content={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {menuActions}
            {havePerrmissonToSCAN ? <Popconfirm
              title="Вы уверены, что хотите удалить?"
              onConfirm={handleRemoveReport}
              okText="Удалить"
              cancelText="Отмена"
              placement="top"
            >
              {renderMenuItem(removeAction)}
            </Popconfirm> : null}
          </div>
        }
        // placement="bottomRight"
        style={{ borderRadius: '10px' }}
        trigger={trigger}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <div
          style={{ marginLeft: '10px', cursor: 'pointer' }}
        >
          <EllipsisOutlined />
        </div>
      </Popover>}
      {modalActions}
    </>
  );
};
